import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface SideLink { label: string; path: string; icon: React.ReactNode; }
export interface SideSection { label: string; links: SideLink[]; }

const matches = (pathname: string, path: string) => pathname === path || pathname.startsWith(path + '/');

/** The link that owns the current path: the longest matching prefix, so "/stocks" only wins on its own page. */
export function currentLink(sections: SideSection[], pathname: string): string | null {
  let best: string | null = null;
  sections.forEach(section => section.links.forEach(link => {
    if (matches(pathname, link.path) && (best === null || link.path.length > best.length)) best = link.path;
  }));
  return best;
}

export function SideNavItem({ link, selected, onClick }: { link: SideLink; selected: boolean; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={selected ? 'page' : undefined}
      sx={theme => ({
        width: '100%', justifyContent: 'flex-start', gap: 1.5, px: 1.5, py: 0.9, borderRadius: 2.5,
        color: selected ? 'primary.dark' : 'text.secondary',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.11) : 'transparent',
        fontWeight: selected ? 650 : 500, fontSize: 14.5, textAlign: 'left',
        transition: 'background-color 120ms, color 120ms',
        '&:hover': { bgcolor: selected ? alpha(theme.palette.primary.main, 0.14) : 'action.hover', color: selected ? 'primary.dark' : 'text.primary' },
        '& svg': { fontSize: 20, color: selected ? 'primary.main' : 'inherit' },
        '& > span:first-of-type': { display: 'flex', fontSize: 18, lineHeight: 1 },
      })}
    >
      <span>{link.icon}</span>
      <span>{link.label}</span>
    </ButtonBase>
  );
}

/** Grouped links with small overline headings, as in every section sidebar. */
export function SideNavSections({ sections, selected, onNavigate }: {
  sections: SideSection[]; selected: string | null; onNavigate: (path: string) => void;
}) {
  return (
    <>
      {sections.map(section => (
        <Box key={section.label} sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary" component="div" sx={{ px: 1.5, mb: 0.25, opacity: 0.8 }}>{section.label}</Typography>
          {section.links.map(link => (
            <SideNavItem key={link.path} link={link} selected={link.path === selected} onClick={() => onNavigate(link.path)} />
          ))}
        </Box>
      ))}
    </>
  );
}
